<template>
  <div class="source-prompt-lab">
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="processType"
          :items="processItems"
          label="Type de process"
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="siteId"
          :items="siteItems"
          label="Site"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col v-if="processType !== 'full_auto_kw'" cols="12" md="4">
        <v-select
          v-model="selectedId"
          :items="templateItems"
          label="Template"
          outlined
          dense
          hide-details
          :loading="loadingTemplates"
          :no-data-text="loadingTemplates ? 'Chargement…' : 'Aucun template'"
          @change="onSelectTemplate"
        />
      </v-col>
    </v-row>

    <!-- ============ VIA ANGLES (une étape) ============ -->
    <template v-if="processType === 'via_angles'">
      <v-textarea
        v-model="promptText"
        label="Prompt (user)"
        class="mt-4"
        outlined
        auto-grow
        rows="10"
        :disabled="!selectedId"
        hide-details
      />

      <v-row dense class="mt-4" align="center">
        <v-col cols="12" md="8">
          <v-text-field
            v-model="topic"
            label="Topic / angle à tester"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex" style="gap: 8px">
          <v-btn
            color="primary"
            :loading="running"
            :disabled="!canRun"
            @click="runPreview"
          >
            Lancer
          </v-btn>
          <v-btn
            color="success"
            outlined
            :loading="saving"
            :disabled="!selectedId || !promptText.trim()"
            @click="savePrompt"
          >
            Sauvegarder
          </v-btn>
        </v-col>
      </v-row>

      <v-card v-if="preview" outlined class="mt-4 pa-4">
        <div class="text-subtitle-2 mb-2">
          Sources trouvées ({{ preview.urls ? preview.urls.length : 0 }})
          <span class="grey--text text--darken-1">
            — {{ preview.provider_id }} / {{ preview.model_id }}
          </span>
        </div>
        <ul v-if="preview.urls && preview.urls.length" class="mb-3">
          <li v-for="(u, i) in preview.urls" :key="i">
            <a :href="u" target="_blank" rel="noopener">{{ u }}</a>
          </li>
        </ul>
        <div v-else class="grey--text mb-3">Aucune URL renvoyée.</div>

        <v-btn
          small
          color="secondary"
          class="mb-2"
          :disabled="!preview.urls || !preview.urls.length"
          @click="sendToRedaction"
        >
          Envoyer vers rédaction →
        </v-btn>

        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header
              >Prompt rendu (debug)</v-expansion-panel-header
            >
            <v-expansion-panel-content>
              <pre class="prompt-rendered">{{ preview.rendered_prompt }}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </template>

    <!-- ============ VIA KW (chaîne stage1 -> stage2 -> stage3) ============ -->
    <template v-else-if="processType === 'via_kw'">
      <v-row dense class="mt-4" align="center">
        <v-col cols="12" md="8">
          <v-text-field
            v-model="topic"
            label="Mot-clé à tester (entrée du stage 1)"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-btn
            color="primary"
            block
            :loading="running"
            :disabled="!canRunKw"
            @click="runKwChain"
          >
            Lancer la chaîne
          </v-btn>
        </v-col>
      </v-row>

      <v-card
        v-for="stage in kwStageDefs"
        :key="stage.key"
        outlined
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2">{{ stage.label }}</div>
          <v-spacer />
          <v-btn
            small
            color="success"
            outlined
            :loading="savingStage === stage.key"
            :disabled="!selectedId || !kwTexts[stage.key].trim()"
            @click="saveStage(stage.key)"
          >
            Sauvegarder ce stage
          </v-btn>
        </div>

        <v-textarea
          v-model="kwTexts[stage.key]"
          label="Prompt (user)"
          outlined
          auto-grow
          rows="8"
          :disabled="!selectedId"
          hide-details
        />

        <template v-if="stageOutput(stage.key)">
          <div class="text-caption grey--text mt-3">
            {{ stageOutput(stage.key).provider_id }} /
            {{ stageOutput(stage.key).model_id }}
          </div>
          <v-expansion-panels flat class="mt-1">
            <v-expansion-panel>
              <v-expansion-panel-header
                >Sortie du stage</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  formatOutput(stageOutput(stage.key).output)
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Prompt rendu (debug)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  stageOutput(stage.key).rendered_prompt
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card>
    </template>

    <!-- ============ FULL-AUTO VIA KW (AIF_KWAUTO, 5 stages) ============ -->
    <template v-else-if="processType === 'full_auto_kw'">
      <v-row dense class="mt-4" align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="nbArticlesMax"
            type="number"
            min="1"
            label="Nb articles max (clustering)"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-btn
            color="primary"
            :loading="running"
            :disabled="!canRunKwauto"
            @click="runKwautoChain"
          >
            Lancer la chaîne (échantillon Marfeel live)
          </v-btn>
          <span class="text-caption grey--text ml-3">
            Récupère un échantillon Google Discover réel du site, déroule les 5
            étapes sur 1 cluster. Ne publie rien.
          </span>
        </v-col>
      </v-row>

      <v-card
        v-if="kwautoPreview && kwautoPreview.master_url"
        outlined
        class="mt-4 pa-4"
      >
        <div class="text-subtitle-2 mb-1">Master URL scrapée</div>
        <a :href="kwautoPreview.master_url" target="_blank" rel="noopener">
          {{ kwautoPreview.master_url }}
        </a>
        <v-expansion-panels flat class="mt-2">
          <v-expansion-panel>
            <v-expansion-panel-header>
              Échantillon Marfeel ({{
                kwautoPreview.marfeel_sample
                  ? kwautoPreview.marfeel_sample.length
                  : 0
              }}) + contenu scrapé
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <pre class="prompt-rendered">{{
                formatOutput(kwautoPreview.marfeel_sample)
              }}</pre>
              <div class="text-caption grey--text mt-2">
                Contenu (extrait) :
              </div>
              <pre class="prompt-rendered">{{
                kwautoPreview.content_preview
              }}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <v-card
        v-for="step in kwautoPrompts"
        :key="step.step"
        outlined
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2">{{ step.label }} ({{ step.step }})</div>
          <v-spacer />
          <v-btn
            small
            color="success"
            outlined
            :loading="savingKwStep === step.step"
            :disabled="!step.binding_id || !kwautoTexts[step.step]"
            @click="saveKwautoStep(step)"
          >
            Sauvegarder ce stage
          </v-btn>
        </div>

        <v-alert v-if="!step.binding_id" type="warning" dense text class="mb-2">
          Aucun prompt AIF_KWAUTO résolu pour ce step / site.
        </v-alert>

        <v-textarea
          v-model="kwautoTexts[step.step]"
          label="Prompt (user)"
          outlined
          auto-grow
          rows="8"
          :disabled="!step.binding_id"
          hide-details
        />

        <template v-if="kwautoStageOutput(step.step)">
          <div class="text-caption grey--text mt-3">
            {{ kwautoStageOutput(step.step).provider_id }} /
            {{ kwautoStageOutput(step.step).model_id }}
          </div>
          <v-expansion-panels flat class="mt-1">
            <v-expansion-panel>
              <v-expansion-panel-header
                >Sortie du stage</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  kwautoStageOutput(step.step).output
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Prompt rendu (debug)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="prompt-rendered">{{
                  kwautoStageOutput(step.step).rendered_prompt
                }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card>
    </template>

    <!-- ============ AUTRES (discover) ============ -->
    <v-alert v-else type="info" dense text class="mt-4 mb-0">
      Le process <strong>{{ processType }}</strong> n'est pas encore disponible.
    </v-alert>
  </div>
</template>

<script>
import axios from "@axios";

export default {
  name: "SourcePromptLab",

  data() {
    return {
      processType: "via_angles",
      siteId: null,
      siteList: [],
      templates: [],
      selectedId: null,
      promptText: "",
      topic: "",
      preview: null,
      loadingTemplates: false,
      running: false,
      saving: false,
      savingStage: null,
      kwTexts: { stage1: "", stage2: "", stage3: "" },
      kwPreview: null,
      kwStageDefs: [
        { key: "stage1", label: "Stage 1 — thématiques / angles (Perplexity)" },
        { key: "stage2", label: "Stage 2 — articles (Gemini)" },
        { key: "stage3", label: "Stage 3 — sources / URLs" },
      ],
      kwautoPrompts: [],
      kwautoTexts: {},
      kwautoPreview: null,
      nbArticlesMax: 3,
      savingKwStep: null,
      processItems: [
        { text: "Via Angles", value: "via_angles" },
        { text: "Via Mots-clés", value: "via_kw" },
        { text: "Full-auto (via KW)", value: "full_auto_kw" },
        { text: "Via Discover", value: "discover" },
      ],
    };
  },

  computed: {
    siteItems() {
      const sites = (this.siteList || []).map((s) => ({
        text: s.name || s.label || s.key,
        value: s.id,
      }));
      return [{ text: "Global (tous sites)", value: null }, ...sites];
    },

    templateItems() {
      return this.templates.map((t) => ({
        text: `${t.name}${t.is_default ? " (défaut)" : ""} — ${t.model}`,
        value: t.id,
      }));
    },

    selectedTemplate() {
      return this.templates.find((t) => t.id === this.selectedId) || null;
    },

    canRun() {
      return (
        !!this.selectedId &&
        !!this.promptText.trim() &&
        !!this.topic.trim() &&
        this.processType === "via_angles"
      );
    },

    canRunKw() {
      return (
        !!this.selectedId &&
        !!this.topic.trim() &&
        this.processType === "via_kw"
      );
    },

    canRunKwauto() {
      return (
        this.processType === "full_auto_kw" &&
        this.kwautoPrompts.some((p) => p.binding_id)
      );
    },
  },

  watch: {
    processType() {
      this.reloadForProcess();
    },
    siteId() {
      this.reloadForProcess();
    },
  },

  mounted() {
    this.fetchSites();
    this.reloadForProcess();
  },

  methods: {
    async fetchSites() {
      try {
        this.siteList = await this.$store.dispatch("common/getSiteList");
      } catch (error) {
        this.$toast?.error("Erreur chargement des sites: " + error.message) ||
          console.error(error);
      }
    },

    reloadForProcess() {
      if (this.processType === "full_auto_kw") {
        this.fetchKwautoPrompts();
      } else {
        this.fetchTemplates();
      }
    },

    async fetchTemplates() {
      this.loadingTemplates = true;
      this.templates = [];
      this.selectedId = null;
      this.promptText = "";
      this.preview = null;
      this.kwPreview = null;
      this.kwTexts = { stage1: "", stage2: "", stage3: "" };
      try {
        const { data } = await axios.get(
          "/api/ai-factory/sandbox/source-prompts",
          { params: { process_type: this.processType, site_id: this.siteId } }
        );
        this.templates = data.items || [];
        if (this.templates.length) {
          this.selectedId = this.templates[0].id;
          this.onSelectTemplate();
        }
      } catch (error) {
        this.$toast?.error(
          "Erreur chargement des templates: " + error.message
        ) || console.error(error);
      } finally {
        this.loadingTemplates = false;
      }
    },

    onSelectTemplate() {
      this.preview = null;
      this.kwPreview = null;
      const t = this.selectedTemplate;
      this.promptText = t ? t.stage_default || "" : "";
      this.kwTexts = {
        stage1: t ? t.stage1 || "" : "",
        stage2: t ? t.stage2 || "" : "",
        stage3: t ? t.stage3 || "" : "",
      };
    },

    async runPreview() {
      this.running = true;
      this.preview = null;
      try {
        const { data } = await axios.post(
          `/api/ai-factory/sandbox/source-prompts/${this.selectedId}/preview`,
          { topic: this.topic, user_prompt: this.promptText }
        );
        this.preview = data;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur Lancer: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.running = false;
      }
    },

    async savePrompt() {
      const ok = window.confirm(
        "Sauvegarder ce prompt ? Il deviendra la version active utilisée en production."
      );
      if (!ok) return;
      this.saving = true;
      try {
        await axios.patch(
          `/api/ai-factory/sandbox/source-prompts/${this.selectedId}`,
          { stage_default: this.promptText }
        );
        this.$toast?.success("Prompt sauvegardé (nouvelle révision active)") ||
          console.log("Prompt saved");
        await this.fetchTemplatesKeepSelection();
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur sauvegarde: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.saving = false;
      }
    },

    async fetchTemplatesKeepSelection() {
      const keep = this.selectedId;
      try {
        const { data } = await axios.get(
          "/api/ai-factory/sandbox/source-prompts",
          { params: { process_type: this.processType, site_id: this.siteId } }
        );
        this.templates = data.items || [];
        if (this.templates.find((t) => t.id === keep)) {
          this.selectedId = keep;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async runKwChain() {
      this.running = true;
      this.kwPreview = null;
      try {
        const { data } = await axios.post(
          `/api/ai-factory/sandbox/source-prompts/${this.selectedId}/preview`,
          {
            topic: this.topic,
            stage1: this.kwTexts.stage1,
            stage2: this.kwTexts.stage2,
            stage3: this.kwTexts.stage3,
          }
        );
        this.kwPreview = data;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur Lancer: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.running = false;
      }
    },

    async saveStage(step) {
      const ok = window.confirm(
        `Sauvegarder le prompt ${step} ? Il deviendra la version active utilisée en production.`
      );
      if (!ok) return;
      this.savingStage = step;
      try {
        await axios.patch(
          `/api/ai-factory/sandbox/source-prompts/${this.selectedId}`,
          { [step]: this.kwTexts[step] }
        );
        this.$toast?.success(
          `Prompt ${step} sauvegardé (nouvelle révision active)`
        ) || console.log("stage saved");
        await this.fetchTemplatesKeepSelection();
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur sauvegarde: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.savingStage = null;
      }
    },

    sendToRedaction() {
      this.$emit("send-to-redaction", {
        topic: this.topic,
        sources: (this.preview?.urls || []).map((u) => ({ url: u })),
        siteId: this.siteId,
      });
    },

    stageOutput(step) {
      if (!this.kwPreview || !this.kwPreview.stages) return null;
      return this.kwPreview.stages.find((s) => s.step === step) || null;
    },

    formatOutput(output) {
      try {
        return JSON.stringify(output, null, 2);
      } catch (e) {
        return String(output);
      }
    },

    async fetchKwautoPrompts() {
      this.kwautoPrompts = [];
      this.kwautoTexts = {};
      this.kwautoPreview = null;
      try {
        const { data } = await axios.get(
          "/api/ai-factory/sandbox/kwauto-prompts",
          { params: { site_id: this.siteId } }
        );
        this.kwautoPrompts = data.items || [];
        const texts = {};
        this.kwautoPrompts.forEach((p) => {
          texts[p.step] = p.content || "";
        });
        this.kwautoTexts = texts;
      } catch (error) {
        this.$toast?.error(
          "Erreur chargement prompts full-auto: " + error.message
        ) || console.error(error);
      }
    },

    async runKwautoChain() {
      this.running = true;
      this.kwautoPreview = null;
      try {
        const overrides = {};
        this.kwautoPrompts.forEach((p) => {
          overrides[p.step] = this.kwautoTexts[p.step];
        });
        const { data } = await axios.post(
          "/api/ai-factory/sandbox/kwauto-prompts/preview",
          {
            site_id: this.siteId,
            nb_articles_max: this.nbArticlesMax,
            ...overrides,
          }
        );
        this.kwautoPreview = data;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur Lancer: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.running = false;
      }
    },

    async saveKwautoStep(step) {
      const ok = window.confirm(
        `Sauvegarder le prompt ${step.step} ? Il deviendra la version active utilisée en production.`
      );
      if (!ok) return;
      this.savingKwStep = step.step;
      try {
        await axios.patch(
          `/api/ai-factory/sandbox/kwauto-prompts/${step.binding_id}`,
          { content: this.kwautoTexts[step.step] }
        );
        this.$toast?.success(
          `Prompt ${step.step} sauvegardé (nouvelle révision active)`
        ) || console.log("kwauto stage saved");
      } catch (error) {
        const detail = error?.response?.data?.detail;
        this.$toast?.error("Erreur sauvegarde: " + (detail || error.message)) ||
          console.error(error);
      } finally {
        this.savingKwStep = null;
      }
    },

    kwautoStageOutput(step) {
      if (!this.kwautoPreview || !this.kwautoPreview.stages) return null;
      return this.kwautoPreview.stages.find((s) => s.step === step) || null;
    },
  },
};
</script>

<style scoped>
.prompt-rendered {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  margin: 0;
}
</style>
